<template>
	<span class="secs-timer tt-timer" :class="extraClasses">{{ formatted }}</span>
</template>

<script setup lang="ts">
import {ref, computed, watchEffect, watch, onUnmounted} from "vue";

const props = withDefaults(defineProps<{
	run?: boolean;
	size?: "small"|"normal"|"big"|"mega";
	countdown?: boolean;
	/** Used to detect the change from the outside */
	created?: number;
}>(), {
	run:false,
	size: "normal",
	countdown: false,
	created: 0
});
const secs = defineModel<number>();
const emit = defineEmits(["done"]);	// emited when the timer reaches the destination (final time)

const runStart = ref<number>(Date.now());
const runStartSecs = ref<number>(secs.value);

const extraClasses = computed(() => {
	return ["secs-timer-" + props.size];
});

function padZero(str:string):string {
	if (str.length === 0) return "00";
	else if (str.length === 1) return "0"+str;
	return str;
}
const formatted = computed<string>(() => {
	let total = Math.round(isNaN(secs.value) ? 0 : secs.value);
	const s = total % 60;
	total = (total - s) / 60;
	const m = total % 60;
	total = (total - m) / 60;
	let ms = padZero(m.toString()) + ":" + padZero(s.toString());
	if (total > 0) ms = total.toString() + ":" + ms;
	return ms;
});

function updateSeconds() {
	const msDiff = Date.now() - runStart.value;
	if (props.countdown) {
		const diff = runStartSecs.value - msDiff / 1000;
		if (diff <= 0) {
			clearInterval(runTimer);
			runTimer = 0;
			emit("done");
		}
		secs.value = Math.max(Math.round(diff), 0);
	} else {
		secs.value = Math.round(runStartSecs.value + msDiff / 1000);
	}
}

let runTimer = 0;
watchEffect(() => {
	if (props.run) {
		if (!runTimer) {
			runStart.value = Date.now();
			runStartSecs.value = secs.value;
			runTimer = setInterval(updateSeconds, 1000);
		}
	} else {
		if (runTimer) {
			clearInterval(runTimer);
			runTimer = 0;
		}
	}
});

/** Detect seconds change from the outside and resync the timer */
watch(() => props.created, (created) => {
	runStart.value = Date.now();
	runStartSecs.value = secs.value;
});

onUnmounted(() => {
	if (runTimer) {
		clearInterval(runTimer);
	}
})
</script>

<style>
.secs-timer.tt-timer {
	transition: all 250ms;

	&.secs-timer-small { font-size: 12px }
	&.secs-timer-normal { font-size: 14px }
	&.secs-timer-big { font-size: 21px }
	&.secs-timer-mega { font-size: 28px }
}
</style>